import { useEffect, useMemo, useState } from "react";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { downloadCsv } from "../lib/csv.js";
import { formatMoney, formatPct, toNumber } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { useT } from "../i18n/index.js";

type StatusRec = "Sem fatura" | "Pendente" | "Recebido Parcial" | "Recebido Total";
type StatusPag = "Sem fatura" | "Aguardando" | "Pago Parcial" | "Pago Total";

const STATUS_REC_TONE: Record<StatusRec, string> = {
  "Sem fatura": "bg-neutral-700/40 text-neutral-300",
  Pendente: "bg-amber-500/15 text-amber-300",
  "Recebido Parcial": "bg-blue-500/15 text-blue-300",
  "Recebido Total": "bg-lime/15 text-lime",
};
const STATUS_PAG_TONE: Record<StatusPag, string> = {
  "Sem fatura": "bg-neutral-700/40 text-neutral-300",
  Aguardando: "bg-amber-500/15 text-amber-300",
  "Pago Parcial": "bg-blue-500/15 text-blue-300",
  "Pago Total": "bg-lime/15 text-lime",
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function currentMonthRange(): { from: string; to: string } {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const from = new Date(year, month, 1).toISOString().slice(0, 10);
  const to = new Date(year, month + 1, 0).toISOString().slice(0, 10);
  return { from, to };
}

function monthsAgo(n: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
}

function formatDateBr(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

export default function Relatorio() {
  const t = useT();
  const { user } = useAuth();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  // Promote first accessible company once companies.list lands (useState
  // init runs only once with empty array on initial render).

  // Filtros (editados livremente; só vão pra query ao clicar "Gerar")
  const initialRange = currentMonthRange();
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [technicianId, setTechnicianId] = useState<number | "all">("all");
  const [customerId, setCustomerId] = useState<number | "all">("all");
  const [billingCompanyId, setBillingCompanyId] = useState<number | "all">("all");
  const [statusRec, setStatusRec] = useState<"all" | "pending" | "partial" | "received">("all");
  const [statusPag, setStatusPag] = useState<"all" | "pending" | "paid" | "cancelled">("all");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [q, setQ] = useState("");

  // Filtros aplicados (snapshot enviado para a query)
  const [applied, setApplied] = useState({
    from: initialRange.from,
    to: initialRange.to,
    technicianId: "all" as number | "all",
    customerId: "all" as number | "all",
    billingCompanyId: "all" as number | "all",
    statusRec: "all" as "all" | "pending" | "partial" | "received",
    statusPag: "all" as "all" | "pending" | "paid" | "cancelled",
    invoiceNumber: "",
    q: "",
  });

  const techsQuery = trpc.technicians.list.useQuery(
    { companyId: companyId ?? 0, active: true, limit: 200, offset: 0 },
    { enabled: companyId !== null },
  );
  const customersQuery = trpc.customers.list.useQuery(
    { companyId: companyId ?? 0, limit: 200, offset: 0 },
    { enabled: companyId !== null },
  );

  const filters = useMemo(
    () => ({
      companyId: companyId ?? 0,
      from: applied.from || undefined,
      to: applied.to || undefined,
      technicianId: applied.technicianId === "all" ? undefined : applied.technicianId,
      customerId: applied.customerId === "all" ? undefined : applied.customerId,
      billingCompanyId: applied.billingCompanyId === "all" ? undefined : applied.billingCompanyId,
      customerReceiptStatus: applied.statusRec === "all" ? undefined : applied.statusRec,
      technicianPaymentStatus: applied.statusPag === "all" ? undefined : applied.statusPag,
      invoiceNumber: applied.invoiceNumber || undefined,
      q: applied.q || undefined,
    }),
    [companyId, applied],
  );

  const reportQuery = trpc.serviceLines.exportConsolidado.useQuery(filters, {
    enabled: companyId !== null,
  });

  const rows = reportQuery.data?.rows ?? [];
  const truncated = reportQuery.data?.truncated ?? false;

  // Totais (client-side, sobre o conjunto retornado)
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, l) => ({
        valorOrcamento: acc.valorOrcamento + (toNumber(l.valorOrcamento) ?? 0),
        valorFaturado: acc.valorFaturado + (toNumber(l.valorFaturado) ?? 0),
        valorAReceber: acc.valorAReceber + (toNumber(l.valorAReceber) ?? 0),
        valorAPagarTecnico: acc.valorAPagarTecnico + (toNumber(l.valorAPagarTecnico) ?? 0),
        valorPagoTecnico: acc.valorPagoTecnico + (toNumber(l.valorPagoTecnico) ?? 0),
        lucroPrevisto: acc.lucroPrevisto + (toNumber(l.lucroPrevisto) ?? 0),
      }),
      {
        valorOrcamento: 0,
        valorFaturado: 0,
        valorAReceber: 0,
        valorAPagarTecnico: 0,
        valorPagoTecnico: 0,
        lucroPrevisto: 0,
      },
    );
  }, [rows]);

  // Resumo por empresa de faturamento
  const porEmpresa = useMemo(() => {
    const map = new Map<
      number,
      {
        id: number;
        name: string;
        faturado: number;
        aReceber: number;
        lucroPrevisto: number;
        count: number;
      }
    >();
    for (const l of rows) {
      const key = l.billingCompanyId;
      const name = l.empresaFaturamento ?? "?";
      const entry = map.get(key) ?? {
        id: key,
        name,
        faturado: 0,
        aReceber: 0,
        lucroPrevisto: 0,
        count: 0,
      };
      entry.faturado += toNumber(l.valorFaturado) ?? 0;
      entry.aReceber += toNumber(l.valorAReceber) ?? 0;
      entry.lucroPrevisto += toNumber(l.lucroPrevisto) ?? 0;
      entry.count += 1;
      map.set(key, entry);
    }
    return Array.from(map.values());
  }, [rows]);

  // Alertas de lucro negativo
  const alertas = useMemo(() => rows.filter((l) => (toNumber(l.lucroPrevisto) ?? 0) < 0), [rows]);

  function applyFilters() {
    setApplied({
      from,
      to,
      technicianId,
      customerId,
      billingCompanyId,
      statusRec,
      statusPag,
      invoiceNumber,
      q,
    });
  }

  function setQuickRange(months: number) {
    setFrom(monthsAgo(months));
    setTo(todayIso());
  }

  function exportCsv() {
    const headers = [
      t("report.colDate"),
      t("report.csvDossier"),
      t("report.invoiceNumber"),
      t("report.colPlate"),
      t("report.csvVehicle"),
      t("report.customer"),
      t("report.technician"),
      t("report.billingCompany"),
      t("report.csvPayingCompany"),
      t("report.quote"),
      t("report.invoiced"),
      t("report.toReceive"),
      t("report.csvPctTechnician"),
      t("report.csvPayTechnician"),
      t("report.csvPaidTechnician"),
      t("report.expectedProfit"),
      t("report.receiptStatus"),
      t("report.csvPaymentStatus"),
    ];
    const data = rows.map((l) => [
      formatDateBr(l.dataFaturamento),
      l.dossierCode,
      l.numeroFatura ?? "",
      l.matriculaVeiculo ?? "",
      [l.vehicleMakeSnapshot, l.vehicleModelSnapshot].filter(Boolean).join(" "),
      l.cliente,
      l.tecnico,
      l.empresaFaturamento,
      l.empresaPagamentoTecnico ?? "",
      l.valorOrcamento,
      l.valorFaturado,
      l.valorAReceber,
      l.percentualComissaoTecnico,
      l.valorAPagarTecnico,
      l.valorPagoTecnico,
      l.lucroPrevisto,
      l.statusRecebimentoCliente,
      l.statusPagamentoTecnico,
    ]);
    const stamp = `${applied.from || "sem-inicio"}_${applied.to || "sem-fim"}`;
    downloadCsv(`relatorio_${stamp}.csv`, headers, data);
  }

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{t("report.noCompanyAccess")}</p>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-neutral-400">{t("report.companyLabel")}</label>
        <select
          value={companyId ?? ""}
          onChange={(e) => setCompanyId(Number(e.target.value))}
          className={inputClass + " w-56"}
        >
          {accessibleCompanies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <div className="ml-auto flex gap-2">
          <button
            onClick={exportCsv}
            disabled={rows.length === 0 || reportQuery.isLoading}
            className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
          >
            {t("report.exportCsvBtn")}
          </button>
        </div>
      </div>

      {/* Filtros */}
      <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-3">
        <h3 className="text-xs uppercase tracking-wider text-neutral-500">
          {t("report.filtersTitle")}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-neutral-500">{t("report.dateFrom")}</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-neutral-500">{t("report.dateTo")}</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-neutral-500">{t("report.quickRange")}</label>
            <div className="flex gap-1">
              <button
                onClick={() => setQuickRange(6)}
                className="flex-1 rounded-md border border-neutral-800 hover:border-neutral-700 px-2 py-1.5 text-xs"
              >
                {t("report.sixMonths")}
              </button>
              <button
                onClick={() => setQuickRange(12)}
                className="flex-1 rounded-md border border-neutral-800 hover:border-neutral-700 px-2 py-1.5 text-xs"
              >
                {t("report.twelveMonths")}
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-neutral-500">{t("report.invoiceNumber")}</label>
            <input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder={t("report.invoiceNumberPlaceholder")}
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-neutral-500">{t("report.customer")}</label>
            <select
              value={customerId === "all" ? "all" : String(customerId)}
              onChange={(e) =>
                setCustomerId(e.target.value === "all" ? "all" : Number(e.target.value))
              }
              className={inputClass}
            >
              <option value="all">{t("report.all")}</option>
              {(customersQuery.data?.rows ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-neutral-500">{t("report.technician")}</label>
            <select
              value={technicianId === "all" ? "all" : String(technicianId)}
              onChange={(e) =>
                setTechnicianId(e.target.value === "all" ? "all" : Number(e.target.value))
              }
              className={inputClass}
            >
              <option value="all">{t("report.all")}</option>
              {(techsQuery.data?.rows ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-neutral-500">{t("report.billingCompany")}</label>
            <select
              value={billingCompanyId === "all" ? "all" : String(billingCompanyId)}
              onChange={(e) =>
                setBillingCompanyId(e.target.value === "all" ? "all" : Number(e.target.value))
              }
              className={inputClass}
            >
              <option value="all">{t("report.allFem")}</option>
              {accessibleCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-neutral-500">{t("report.freeSearch")}</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("report.freeSearchPlaceholder")}
              className={inputClass}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-neutral-500">{t("report.receiptStatus")}</label>
            <select
              value={statusRec}
              onChange={(e) =>
                setStatusRec(e.target.value as "all" | "pending" | "partial" | "received")
              }
              className={inputClass}
            >
              <option value="all">{t("report.all")}</option>
              <option value="pending">{t("report.pending")}</option>
              <option value="partial">{t("report.receivedPartial")}</option>
              <option value="received">{t("report.receivedTotal")}</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-neutral-500">
              {t("report.technicianPaymentStatus")}
            </label>
            <select
              value={statusPag}
              onChange={(e) =>
                setStatusPag(e.target.value as "all" | "pending" | "paid" | "cancelled")
              }
              className={inputClass}
            >
              <option value="all">{t("report.all")}</option>
              <option value="pending">{t("report.awaiting")}</option>
              <option value="paid">{t("report.paid")}</option>
              <option value="cancelled">{t("report.cancelled")}</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <button
            onClick={applyFilters}
            disabled={companyId === null}
            className="rounded-md bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-neutral-900 px-4 py-1.5 text-sm font-medium"
          >
            {t("report.generateReport")}
          </button>
        </div>
      </section>

      {/* Alertas */}
      {alertas.length > 0 && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          ⚠ {alertas.length}{" "}
          {t(
            alertas.length === 1
              ? "report.negativeProfitAlertSingular"
              : "report.negativeProfitAlert",
          )}
        </div>
      )}

      {truncated && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          {t("report.truncatedPre")} 5.000 {t("report.truncatedSuf")}
        </div>
      )}

      {/* Resumo por empresa */}
      {porEmpresa.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {porEmpresa.map((emp) => (
            <div key={emp.id} className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold">{emp.name}</p>
                <span className="rounded-md bg-neutral-800 px-2 py-0.5 text-xs text-neutral-400">
                  {emp.count} {t("report.recordsAbbrev")}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Stat
                  label={t("report.invoiced")}
                  value={formatMoney(emp.faturado, { decimals: 2 })}
                />
                <Stat
                  label={t("report.toReceive")}
                  value={formatMoney(emp.aReceber, { decimals: 2 })}
                  tone="amber"
                />
                <Stat
                  label={t("report.expectedProfitAbbrev")}
                  value={formatMoney(emp.lucroPrevisto, { decimals: 2 })}
                  tone={emp.lucroPrevisto < 0 ? "red" : "positive"}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cards de totais */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Card
          label={t("report.quote")}
          value={formatMoney(totals.valorOrcamento, { decimals: 2 })}
        />
        <Card
          label={t("report.invoiced")}
          value={formatMoney(totals.valorFaturado, { decimals: 2 })}
          tone="blue"
        />
        <Card
          label={t("report.toReceive")}
          value={formatMoney(totals.valorAReceber, { decimals: 2 })}
          tone="amber"
        />
        <Card
          label={t("report.payTechnicians")}
          value={formatMoney(totals.valorAPagarTecnico, { decimals: 2 })}
          tone="violet"
        />
        <Card
          label={t("report.paidTechnicians")}
          value={formatMoney(totals.valorPagoTecnico, { decimals: 2 })}
          tone="positive"
        />
        <Card
          label={t("report.expectedProfit")}
          value={formatMoney(totals.lucroPrevisto, { decimals: 2 })}
          tone={totals.lucroPrevisto < 0 ? "red" : "positive"}
        />
      </div>

      {/* Tabela */}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold">{t("report.entriesTitle")}</h3>
          <span className="text-xs text-neutral-500">
            {rows.length} {t(rows.length === 1 ? "report.recordWordSingular" : "report.recordWord")}
          </span>
        </div>
        {reportQuery.isLoading ? (
          <p className="p-4 text-sm text-neutral-500">{t("report.loading")}</p>
        ) : reportQuery.error ? (
          <p className="p-4 text-sm text-red-400">{reportQuery.error.message}</p>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-neutral-500">{t("report.emptyState")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-3 py-2 text-left whitespace-nowrap">{t("report.colDate")}</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">{t("report.colPlate")}</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">
                    {t("report.colCustomer")}
                  </th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">
                    {t("report.colTechnician")}
                  </th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">
                    {t("report.colCompany")}
                  </th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">{t("report.colQuote")}</th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">
                    {t("report.colInvoiced")}
                  </th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">
                    {t("report.colToReceive")}
                  </th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">
                    {t("report.colPctTech")}
                  </th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">
                    {t("report.colPayTech")}
                  </th>
                  <th className="px-3 py-2 text-right whitespace-nowrap">
                    {t("report.colExpectedProfit")}
                  </th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">
                    {t("report.colReceipt")}
                  </th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">
                    {t("report.colPayTechStatus")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => {
                  const lucro = toNumber(l.lucroPrevisto) ?? 0;
                  const recTone = STATUS_REC_TONE[l.statusRecebimentoCliente];
                  const pagTone = STATUS_PAG_TONE[l.statusPagamentoTecnico];
                  return (
                    <tr
                      key={l.id}
                      className={`border-t border-neutral-800 ${lucro < 0 ? "bg-red-500/5" : ""}`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap">
                        {formatDateBr(l.dataFaturamento)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                        {l.matriculaVeiculo ?? "—"}
                      </td>
                      <td className="px-3 py-2 max-w-[160px] truncate" title={l.cliente}>
                        {l.cliente}
                      </td>
                      <td className="px-3 py-2 max-w-[140px] truncate" title={l.tecnico}>
                        {l.tecnico}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-neutral-300">
                        {l.empresaFaturamento}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(l.valorOrcamento, { decimals: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {formatMoney(l.valorFaturado, { decimals: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(l.valorAReceber, { decimals: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatPct(l.percentualComissaoTecnico, { alreadyPct: true })}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(l.valorAPagarTecnico, { decimals: 2 })}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums font-semibold ${
                          lucro < 0 ? "text-red-300" : "text-lime"
                        }`}
                      >
                        {formatMoney(l.lucroPrevisto, { decimals: 2 })}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`rounded-md px-2 py-0.5 text-xs ${recTone}`}>
                          {l.statusRecebimentoCliente}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`rounded-md px-2 py-0.5 text-xs ${pagTone}`}>
                          {l.statusPagamentoTecnico}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-neutral-700 bg-neutral-950/30 font-semibold">
                  <td
                    colSpan={5}
                    className="px-3 py-2 text-xs uppercase tracking-wider text-neutral-400"
                  >
                    {t("report.totalsRow")}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(totals.valorOrcamento, { decimals: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(totals.valorFaturado, { decimals: 2 })}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(totals.valorAReceber, { decimals: 2 })}
                  </td>
                  <td />
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(totals.valorAPagarTecnico, { decimals: 2 })}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums ${
                      totals.lucroPrevisto < 0 ? "text-red-300" : "text-lime"
                    }`}
                  >
                    {formatMoney(totals.lucroPrevisto, { decimals: 2 })}
                  </td>
                  <td colSpan={2} />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Shell>
  );
}

const inputClass =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function Card({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "blue" | "amber" | "violet" | "positive" | "red";
}) {
  const toneClass =
    tone === "blue"
      ? "text-blue-300"
      : tone === "amber"
        ? "text-amber-300"
        : tone === "violet"
          ? "text-violet-300"
          : tone === "positive"
            ? "text-lime"
            : tone === "red"
              ? "text-red-300"
              : "text-neutral-100";
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-3">
      <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">{label}</p>
      <p className={`text-base font-semibold tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "amber" | "positive" | "red";
}) {
  const toneClass =
    tone === "amber"
      ? "text-amber-300"
      : tone === "positive"
        ? "text-lime"
        : tone === "red"
          ? "text-red-300"
          : "text-neutral-100";
  return (
    <div className="rounded-md bg-neutral-950/40 p-2">
      <p className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</p>
      <p className={`text-xs font-semibold tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <div className="space-y-4">{children}</div>
    </ErpShell>
  );
}
